// client/src/components/bracket/BracketViewport.js
import React, { useRef, useState, useCallback, useEffect } from 'react'
import './BracketViewport.css'

/**
 * BracketViewport - Core container for bracket display with pan/zoom functionality
 *
 * This component handles:
 * - Mouse and touch pan interactions with momentum
 * - Mouse wheel and touch pinch zoom with bounds checking
 * - Fit-to-width functionality
 * - Smooth animations and transitions
 * - Coordinate transformations for child components
 */
function BracketViewport({
  children,
  className = '',
  minZoom = 0.1,
  maxZoom = 3.0,
  fitPadding = 20,
  enableFitToWidth = true,
  onTransformChange,
  velocityNormalizeMs = 1000 / 60, // ~16.67
  ...otherProps
}) {
  const viewportRef = useRef(null)
  const contentRef = useRef(null)
  const [transform, setTransform] = useState({ x: 0, y: 0, scale: 1 })
  const [isDragging, setIsDragging] = useState(false)
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 })
  // Use ref for velocity to avoid stale closures in rAF loop
  const velocityRef = useRef({ x: 0, y: 0 })
  const animationFrameRef = useRef(null)
  const lastMoveTimeRef = useRef(Date.now())
  const lastPositionRef = useRef({ x: 0, y: 0 })

  // Clamp zoom to bounds
  const clampZoom = useCallback(
    (scale) => {
      return Math.max(minZoom, Math.min(maxZoom, scale))
    },
    [minZoom, maxZoom],
  )

  // Get viewport bounds
  const getViewportBounds = useCallback(() => {
    if (!viewportRef.current) return { width: 0, height: 0 }
    const rect = viewportRef.current.getBoundingClientRect()
    return { width: rect.width, height: rect.height }
  }, [])

  // Apply momentum after dragging stops using refs to avoid stale closures
  const stepMomentum = useCallback(() => {
    const { x, y } = velocityRef.current
    if (Math.abs(x) < 0.1 && Math.abs(y) < 0.1) {
      velocityRef.current = { x: 0, y: 0 }
      animationFrameRef.current = null
      return
    }

    setTransform((prev) => ({
      ...prev,
      x: prev.x + x,
      y: prev.y + y,
    }))

    // Dampen velocity directly on the ref
    velocityRef.current = { x: x * 0.95, y: y * 0.95 }

    animationFrameRef.current = requestAnimationFrame(stepMomentum)
  }, [])

  // Start/stop momentum loop based on dragging state only
  useEffect(() => {
    if (!isDragging) {
      const { x, y } = velocityRef.current
      if (Math.abs(x) > 0.1 || Math.abs(y) > 0.1) {
        if (!animationFrameRef.current) {
          animationFrameRef.current = requestAnimationFrame(stepMomentum)
        }
      }
    } else {
      // If dragging resumes, cancel any momentum frame
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current)
        animationFrameRef.current = null
      }
    }

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current)
        animationFrameRef.current = null
      }
    }
  }, [isDragging, stepMomentum])

  // Handle mouse down
  const handleMouseDown = useCallback((e) => {
    if (e.button !== 0) return // Only left mouse button
    // Require Shift to initiate drag to avoid accidental panning while scrolling
    if (!e.shiftKey) return
    e.preventDefault()

    setIsDragging(true)
    setDragStart({ x: e.clientX, y: e.clientY })
    velocityRef.current = { x: 0, y: 0 }
    lastMoveTimeRef.current = Date.now()
    lastPositionRef.current = { x: e.clientX, y: e.clientY }

    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current)
    }
  }, [])

  // Handle mouse move
  const handleMouseMove = useCallback(
    (e) => {
      if (!isDragging) return
      e.preventDefault()

      const now = Date.now()
      const deltaX = e.clientX - dragStart.x
      const deltaY = e.clientY - dragStart.y

      // Calculate velocity for momentum
      if (now - lastMoveTimeRef.current > 0) {
        const velocityX =
          ((e.clientX - lastPositionRef.current.x) /
            (now - lastMoveTimeRef.current)) *
          velocityNormalizeMs
        const velocityY =
          ((e.clientY - lastPositionRef.current.y) /
            (now - lastMoveTimeRef.current)) *
          velocityNormalizeMs
        velocityRef.current = { x: velocityX, y: velocityY }
      }

      setTransform((prev) => ({
        ...prev,
        x: prev.x + deltaX,
        y: prev.y + deltaY,
      }))

      setDragStart({ x: e.clientX, y: e.clientY })
      lastMoveTimeRef.current = now
      lastPositionRef.current = { x: e.clientX, y: e.clientY }
    },
    [isDragging, dragStart, velocityNormalizeMs],
  )

  // Handle mouse up
  const handleMouseUp = useCallback(() => {
    setIsDragging(false)
  }, [])

  // Handle wheel: zoom with Ctrl/Cmd, pan with Shift; otherwise allow default scroll
  const handleWheel = useCallback(
    (e) => {
      const isZoomGesture = e.ctrlKey || e.metaKey
      const isPanGesture = e.shiftKey

      if (isZoomGesture) {
        e.preventDefault()
        const rect = viewportRef.current.getBoundingClientRect()
        const mouseX = e.clientX - rect.left
        const mouseY = e.clientY - rect.top

        const zoomFactor = e.deltaY > 0 ? 0.9 : 1.1
        const newScale = clampZoom(transform.scale * zoomFactor)

        if (newScale !== transform.scale) {
          const scaleChange = newScale / transform.scale
          setTransform((prev) => ({
            x: prev.x - (mouseX - prev.x) * (scaleChange - 1),
            y: prev.y - (mouseY - prev.y) * (scaleChange - 1),
            scale: newScale,
          }))
        }
      } else if (isPanGesture) {
        e.preventDefault()
        const dx = e.deltaX || 0
        const dy = e.deltaY || 0
        setTransform((prev) => ({
          ...prev,
          x: prev.x - dx,
          y: prev.y - dy,
        }))
      } else {
        // No modifier: allow default scroll behavior
        return
      }
    },
    [transform.scale, clampZoom],
  )

  // Touch handling for mobile
  const touchDataRef = useRef({
    lastTouch: null,
    initialDistance: 0,
    initialScale: 1,
  })

  const handleTouchStart = useCallback(
    (e) => {
      e.preventDefault()

      if (e.touches.length === 1) {
        // Single touch - pan
        const touch = e.touches[0]
        setIsDragging(true)
        setDragStart({ x: touch.clientX, y: touch.clientY })
        velocityRef.current = { x: 0, y: 0 }
        touchDataRef.current.lastTouch = { x: touch.clientX, y: touch.clientY }
        lastMoveTimeRef.current = Date.now()
      } else if (e.touches.length === 2) {
        // Two touches - zoom
        const touch1 = e.touches[0]
        const touch2 = e.touches[1]
        const distance = Math.sqrt(
          Math.pow(touch2.clientX - touch1.clientX, 2) +
            Math.pow(touch2.clientY - touch1.clientY, 2),
        )
        touchDataRef.current.initialDistance = distance
        touchDataRef.current.initialScale = transform.scale
        setIsDragging(false)
      }
    },
    [transform.scale],
  )

  const handleTouchMove = useCallback(
    (e) => {
      e.preventDefault()

      if (e.touches.length === 1 && isDragging) {
        // Single touch pan
        const touch = e.touches[0]
        const now = Date.now()
        const deltaX = touch.clientX - dragStart.x
        const deltaY = touch.clientY - dragStart.y

        if (
          now - lastMoveTimeRef.current > 0 &&
          touchDataRef.current.lastTouch
        ) {
          const velocityX =
            ((touch.clientX - touchDataRef.current.lastTouch.x) /
              (now - lastMoveTimeRef.current)) *
            velocityNormalizeMs
          const velocityY =
            ((touch.clientY - touchDataRef.current.lastTouch.y) /
              (now - lastMoveTimeRef.current)) *
            velocityNormalizeMs
          velocityRef.current = { x: velocityX, y: velocityY }
        }

        setTransform((prev) => ({
          ...prev,
          x: prev.x + deltaX,
          y: prev.y + deltaY,
        }))

        setDragStart({ x: touch.clientX, y: touch.clientY })
        touchDataRef.current.lastTouch = { x: touch.clientX, y: touch.clientY }
        lastMoveTimeRef.current = now
      } else if (e.touches.length === 2) {
        // Two touch zoom (preserve focal point)
        const touch1 = e.touches[0]
        const touch2 = e.touches[1]
        const distance = Math.sqrt(
          Math.pow(touch2.clientX - touch1.clientX, 2) +
            Math.pow(touch2.clientY - touch1.clientY, 2),
        )

        if (touchDataRef.current.initialDistance > 0 && viewportRef.current) {
          const scaleChange = distance / touchDataRef.current.initialDistance
          const newScale = clampZoom(
            touchDataRef.current.initialScale * scaleChange,
          )

          // Compute pinch center in viewport coordinates
          const rect = viewportRef.current.getBoundingClientRect()
          const centerX = (touch1.clientX + touch2.clientX) / 2 - rect.left
          const centerY = (touch1.clientY + touch2.clientY) / 2 - rect.top

          setTransform((prev) => {
            const prevScale = prev.scale
            if (newScale === prevScale) return prev
            const s = newScale / prevScale
            return {
              x: prev.x - (centerX - prev.x) * (s - 1),
              y: prev.y - (centerY - prev.y) * (s - 1),
              scale: newScale,
            }
          })
        }
      }
    },
    [isDragging, dragStart, clampZoom, velocityNormalizeMs],
  )

  const handleTouchEnd = useCallback(() => {
    setIsDragging(false)
    touchDataRef.current.lastTouch = null
  }, [])

  // Fit to width functionality
  const fitToWidth = useCallback(() => {
    if (!viewportRef.current || !contentRef.current || !children) return

    const viewport = getViewportBounds()
    if (viewport.width === 0) return

    // Measure actual content width (transforms don't affect scrollWidth/offsetWidth)
    const measuredScrollWidth = contentRef.current.scrollWidth || 0
    const measuredOffsetWidth = contentRef.current.offsetWidth || 0
    const contentWidth = Math.max(measuredScrollWidth, measuredOffsetWidth)
    if (contentWidth === 0) return

    // Calculate scale to fit content width within viewport minus padding
    const scale = (viewport.width - fitPadding * 2) / contentWidth
    const clampedScale = clampZoom(scale)

    setTransform({
      x: fitPadding,
      y: fitPadding,
      scale: clampedScale,
    })
  }, [getViewportBounds, fitPadding, clampZoom, children])

  // Auto fit on mount if enabled
  useEffect(() => {
    if (enableFitToWidth) {
      // Delay to ensure viewport is sized
      const timer = setTimeout(fitToWidth, 100)
      return () => clearTimeout(timer)
    }
  }, [enableFitToWidth, fitToWidth])

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      if (enableFitToWidth) {
        fitToWidth()
      }
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [enableFitToWidth, fitToWidth])

  // Set up mouse event listeners
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)

      return () => {
        document.removeEventListener('mousemove', handleMouseMove)
        document.removeEventListener('mouseup', handleMouseUp)
      }
    }
  }, [isDragging, handleMouseMove, handleMouseUp])

  // Notify parent of transform changes
  useEffect(() => {
    if (onTransformChange) {
      onTransformChange(transform)
    }
  }, [transform, onTransformChange])

  const containerClasses = [
    'bracket-viewport',
    isDragging ? 'bracket-viewport--dragging' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div
      ref={viewportRef}
      className={containerClasses}
      onMouseDown={handleMouseDown}
      onWheel={handleWheel}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      style={{
        cursor: isDragging ? 'grabbing' : 'grab',
        touchAction: 'none',
      }}
      role="region"
      aria-label="Interactive bracket viewer - Use Shift+drag to pan, Ctrl+scroll to zoom on desktop. Use pinch gestures to zoom on touch devices."
      tabIndex={0}
      {...otherProps}
    >
      <div
        className="bracket-viewport__content"
        ref={contentRef}
        style={{
          transform: `translate(${transform.x}px, ${transform.y}px) scale(${transform.scale})`,
          transformOrigin: '0 0',
          transition: isDragging ? 'none' : 'transform 0.1s ease-out',
        }}
      >
        {children}
      </div>
      <div className="bracket-viewport-controls-hint" aria-hidden="true">
        Desktop: Shift+Drag to pan • Ctrl+Scroll to zoom | Mobile: Drag to pan •
        Pinch to zoom
      </div>
    </div>
  )
}

// Expose fit to width function for external control
BracketViewport.displayName = 'BracketViewport'

export default BracketViewport
